import { useState } from "react";
import { NavLink, Link, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "@/i18n";

const NAV_ITEMS = [
  { key: "services", hash: "#services" },
  { key: "about", hash: "#about" },
  { key: "solutions", hash: "#solutions" },
  { key: "contact", hash: "#contact" },
];

export default function Header() {
  const { t } = useTranslation();
  const { lang: paramLang } = useParams();
  const location = useLocation();
  const lang = SUPPORTED_LANGS.includes(paramLang) ? paramLang : DEFAULT_LANG;
  const [open, setOpen] = useState(false);

  // Restore the "rest of path" after the language segment for language switching
  const parts = location.pathname.split("/").filter(Boolean);
  const restPath = "/" + parts.slice(1).join("/");
  const currentPath = restPath === "/" ? "" : restPath;

  const homeHref = `/${lang}`;

  const onNavClick = () => setOpen(false);

  return (
    <header
      className="w-full border-b border-white/10"
      style={{ backgroundColor: "var(--anv-ink)" }}
      data-testid="site-header"
    >
      <div className="anv-container flex items-center justify-between py-5">
        <Link
          to={homeHref}
          className="flex items-baseline gap-3 text-white no-underline"
          data-testid="header-brand-link"
        >
          <span
            className="font-display text-2xl md:text-[26px] font-semibold tracking-[0.18em]"
            style={{ color: "#fff" }}
          >
            {t("header.brand")}
          </span>
          <span className="hidden md:inline text-[11px] uppercase tracking-[0.25em] text-white/60">
            {t("header.brandTagline")}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav
          className="hidden lg:flex items-center gap-8"
          aria-label="primary"
          data-testid="header-nav-desktop"
        >
          {NAV_ITEMS.map((item) => (
            <a
              key={item.key}
              href={`${homeHref}${item.hash}`}
              className="text-[13px] font-medium tracking-[0.06em] text-white/85 hover:text-white no-underline uppercase"
              data-testid={`nav-link-${item.key}`}
            >
              {t(`header.nav.${item.key}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {/* Language switcher (desktop + mobile) */}
          <div
            className="flex items-center gap-1 text-[12px] font-semibold tracking-[0.1em]"
            role="group"
            aria-label={t("header.language")}
            data-testid="lang-switcher"
          >
            {SUPPORTED_LANGS.map((code, idx) => (
              <span key={code} className="flex items-center">
                <NavLink
                  to={`/${code}${currentPath}`}
                  className={({ isActive: _isActive }) => {
                    const active = code === lang;
                    return `px-1.5 py-1 no-underline uppercase ${
                      active ? "text-white" : "text-white/50 hover:text-white"
                    }`;
                  }}
                  data-testid={`lang-switch-${code}`}
                >
                  {code}
                </NavLink>
                {idx === 0 && <span className="text-white/30 px-0.5">·</span>}
              </span>
            ))}
          </div>

          {/* Desktop CTA */}
          <a
            href={`${homeHref}#newsletter`}
            className="hidden md:inline-flex items-center gap-2 bg-[color:var(--anv-terra)] hover:bg-[color:var(--anv-terra-dark)] text-white font-semibold text-[13px] uppercase tracking-[0.08em] px-4 py-2.5 no-underline"
            style={{ borderRadius: "2px" }}
            data-testid="header-cta-button"
          >
            {t("header.cta")}
          </a>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center p-2 text-white"
            aria-label={open ? t("header.closeMenu") : t("header.openMenu")}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden border-t border-white/10"
          style={{ backgroundColor: "var(--anv-ink)" }}
          data-testid="mobile-menu"
        >
          <div className="anv-container flex flex-col py-4 gap-1">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.key}
                href={`${homeHref}${item.hash}`}
                onClick={onNavClick}
                className="py-3 text-white/90 text-sm uppercase tracking-[0.08em] no-underline border-b border-white/10"
                data-testid={`mobile-nav-link-${item.key}`}
              >
                {t(`header.nav.${item.key}`)}
              </a>
            ))}
            <a
              href={`${homeHref}#newsletter`}
              onClick={onNavClick}
              className="mt-4 inline-flex items-center justify-center gap-2 bg-[color:var(--anv-terra)] hover:bg-[color:var(--anv-terra-dark)] text-white font-semibold text-sm uppercase tracking-[0.08em] px-4 py-3 no-underline"
              style={{ borderRadius: "2px" }}
              data-testid="mobile-header-cta"
            >
              {t("header.cta")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
